// Subscription path (Anthropic Pro/Team/Max plan) — 3 step verify:
//   1) checkClaudeCodeSubscriptionAuth() — `claude auth status` exit code + parse JSON loggedIn
//   2) triggerClaudeCodeAuthLogin()       — `claude auth login` interactive (browser)
//   3) verifyClaudeCodeQuota()            — cheap prompt detect quota/auth issues
//
// Mục đích phase 03: đảm bảo claude binary có thể chạy thật trước khi ghi settings.
// Quan trọng: `auth status loggedIn:true` KHÔNG đảm bảo token còn valid — có thể stale/revoked.
// verifyClaudeCodeQuota phát hiện token expired qua 401 và trả reason=auth-expired.
import { spawnSync } from "node:child_process";
import { isNonInteractive } from "./non-interactive-context.js";
import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";
import { log } from "./terminal-logger.js";

export type ClaudeCodeAuthState = "authenticated" | "not-authenticated";

// v1.12.2: Rich auth info để skip verify quota khi auth status đã đủ tin cậy.
// Cost: 0.5s `claude auth status` thay vì 30-60s `claude --print ok`.
export interface ClaudeCodeAuthInfo {
  state: ClaudeCodeAuthState;
  email?: string;
  subscriptionType?: string; // "max" | "pro" | "team" | ...
  apiProvider?: string; // "firstParty" | ...
}

export interface ClaudeCodeQuotaResult {
  ok: boolean;
  // Reason code chuẩn hóa để caller branch:
  //   "credit_balance_too_low" | "insufficient_quota" | "invalid_api_key"
  //   | "auth-expired" | "rate_limit" | "timeout" | "unknown"
  reason?: string;
  // Raw stderr/stdout sample để debug (truncated 500 chars).
  detail?: string;
}

// Quota verify timeout — claude --print API call thông thường 5-15s, nhưng:
// - Mạng VN chậm + Anthropic API có thể spike 30s+
// - First-call sau auth có thể slow vì load model cache
// 60s threshold safe cho most cases, vẫn không quá lâu để user wait UX.
const QUOTA_VERIFY_TIMEOUT_MS = 60_000;

// Cheap prompt: nội dung tối giản. Cost ~$0.0001 trên Sonnet.
const QUOTA_VERIFY_PROMPT = "ok";

// Step 1: check `claude auth status` exit code + parse JSON output.
// v2.1.x output JSON `{ loggedIn: bool, ... }`. Trust BOTH exit code AND loggedIn flag.
// Nếu exit 0 nhưng loggedIn:false → treat as not-authenticated.
export function checkClaudeCodeSubscriptionAuth(): ClaudeCodeAuthState {
  return readClaudeCodeAuthInfo().state;
}

// v1.12.2: Rich variant — trả về cả email, subscriptionType, apiProvider.
// Dùng để skip verify quota khi auth status đủ tin cậy (subscriptionType set =
// account active có plan, không cần probe thêm `claude --print` 30-60s).
export function readClaudeCodeAuthInfo(): ClaudeCodeAuthInfo {
  const result = spawnSync("claude", ["auth", "status"], { encoding: "utf8" });
  if (result.error || result.status !== 0) return { state: "not-authenticated" };

  const stdout = (result.stdout || "").trim();
  if (!stdout.startsWith("{")) {
    // Older versions plain text output → trust exit code only.
    return { state: "authenticated" };
  }

  try {
    const parsed = JSON.parse(stdout) as {
      loggedIn?: boolean;
      email?: string;
      subscriptionType?: string;
      apiProvider?: string;
    };
    if (parsed.loggedIn !== true) return { state: "not-authenticated" };
    return {
      state: "authenticated",
      email: parsed.email,
      subscriptionType: parsed.subscriptionType,
      apiProvider: parsed.apiProvider,
    };
  } catch {
    // JSON parse fail → trust exit code.
    return { state: "authenticated" };
  }
}

// Step 2: trigger `claude auth login` interactive — block đến khi user complete trên browser.
// Throws nếu spawn fail / user cancel (exit non-zero).
export function triggerClaudeCodeAuthLogin(): void {
  // ⛔ KHÔNG TREO KHI KHÔNG CÓ NGƯỜI: lệnh dưới đây `spawnSync` một luồng đăng
  // nhập OAuth với `stdio: "inherit"` — nó đợi người bấm nút trên browser, vô hạn.
  // Đây KHÔNG phải prompt của @inquirer nên `prompt-or-flag` không chặn được; phải
  // gác riêng tại đây, nếu không `--non-interactive` vẫn treo đúng cái nó sinh ra để diệt.
  if (isNonInteractive()) {
    throw new Error(
      "Chưa đăng nhập Claude Code, mà `claude auth login` cần browser — không chạy được ở chế độ " +
        "non-interactive. Chạy `claude auth login` một lần trên máy này rồi gọi lại.",
    );
  }
  log.info("Khởi động đăng nhập Claude Code (browser sẽ mở)...");
  const result = spawnSync("claude", ["auth", "login"], { stdio: "inherit" });
  if (result.status !== 0) {
    throw new Error(
      `claude auth login thất bại (exit ${result.status}). Thử 'claude auth login' tay rồi chạy lại.`,
    );
  }
  log.success("Đã đăng nhập Claude Code");
}

// Map error keyword → reason code chuẩn hóa.
// Order quan trọng: check specific trước generic.
// Expanded coverage cho stderr message dạng mới (vd "401 Invalid authentication credentials").
function classifyQuotaError(combinedOutput: string): string {
  const text = combinedOutput.toLowerCase();

  // Credit / quota family.
  if (text.includes("credit_balance_too_low") || text.includes("credit balance too low")) {
    return "credit_balance_too_low";
  }
  if (text.includes("insufficient_quota") || text.includes("insufficient quota")) {
    return "insufficient_quota";
  }
  if (
    text.includes("quota_exceeded") ||
    text.includes("quota exceeded") ||
    text.includes("usage limit") ||
    text.includes("you've used all")
  ) {
    return "insufficient_quota";
  }

  // Auth family — token expired / 401 / invalid credentials.
  // Common Anthropic messages: "401 Invalid authentication credentials", "authentication failed",
  // "failed to authenticate", "unauthorized".
  if (
    text.includes("401") ||
    text.includes("invalid authentication") ||
    text.includes("authentication credentials") ||
    text.includes("failed to authenticate") ||
    text.includes("authentication failed") ||
    text.includes("unauthorized")
  ) {
    return "auth-expired";
  }
  if (text.includes("invalid_api_key") || text.includes("invalid api key")) {
    return "invalid_api_key";
  }

  // Rate limit family.
  if (text.includes("rate_limit") || text.includes("rate limit") || text.includes("429")) {
    return "rate_limit";
  }

  return "unknown";
}

// User-facing hint string theo reason code — giúp message cuối cùng actionable.
// Caller (run-ai-setup-phase) sẽ append hint này vào log warn.
export function getQuotaErrorHint(reason: string): string {
  switch (reason) {
    case "auth-expired":
      return "Token Claude Code đã hết hạn/bị revoke. Chạy: `claude auth logout && claude auth login`.";
    case "credit_balance_too_low":
    case "insufficient_quota":
      return "Hết quota subscription. Upgrade plan hoặc dùng LLMLite (avatar ai setup → chọn LLMLite).";
    case "invalid_api_key":
      return "API key invalid. Re-login: `claude auth login`.";
    case "rate_limit":
      return "Bị rate limit tạm thời. Chờ vài phút rồi chạy `avatar ai setup`.";
    case "timeout":
      return "Timeout 60s: (1) mạng VN chậm — thử VPN, (2) Anthropic API spike — retry vài phút, (3) token vẫn auth nhưng quota hết âm thầm — check tại claude.ai/settings/usage.";
    default:
      return "Lỗi chưa biết. Xem stderr ở trên + chạy `claude --print ok` tay để debug.";
  }
}

// Step 3: verify quota qua cheap prompt. Trả structured result.
// Không throw — caller cần ok/reason để decide branch (warn + suggest LLMLite).
// Khi reason="unknown" — log full stderr+stdout để user/dev biết Anthropic đổi message gì.
//
// v1.12 fix: stdin "ignore" thay vì default pipe — tránh race nếu parent đang đợi
// inquirer prompt (stdin TTY buffer leak gây claude --print block/fail intermittent).
// v1.12 fix: dump cả stdout (không chỉ stderr) khi unknown — observed case status≠0
// nhưng stdout có response (Anthropic message qua stdout, exit code lệch).
export async function verifyClaudeCodeQuota(): Promise<ClaudeCodeQuotaResult> {
  // spawn async (không spawnSync): event loop tự do trong lúc chờ claude --print
  // tới 60s; Ctrl-C kill được child. timedOut do spawnAsyncCapture set khi vượt timeout.
  const result = await spawnAsyncCapture("claude", ["--print", QUOTA_VERIFY_PROMPT], {
    timeoutMs: QUOTA_VERIFY_TIMEOUT_MS,
  });

  // timedOut (kill SIGTERM khi vượt timeout) hoặc exit-code 143 (128+SIGTERM).
  const isTimeout = result.timedOut || result.status === 143;
  if (isTimeout) {
    return {
      ok: false,
      reason: "timeout",
      detail: `claude --print > ${QUOTA_VERIFY_TIMEOUT_MS / 1000}s (mạng chậm / API rate limit / token revoked không return error)`,
    };
  }

  const stderr = result.stderr;
  const stdout = result.stdout;

  if (result.status === 0) {
    // Claude trả response — quota OK.
    return { ok: true };
  }

  // Defensive fallback: exit non-zero nhưng stdout có response thật (>20 chars,
  // không có keyword lỗi) → likely false-negative (claude binary quirk).
  // Trust stdout content thay vì exit code.
  const stdoutTrimmed = stdout.trim();
  const stderrLower = stderr.toLowerCase();
  if (
    stdoutTrimmed.length > 20 &&
    !stderrLower.includes("error") &&
    !stderrLower.includes("limit") &&
    !stderrLower.includes("quota") &&
    !stderrLower.includes("401")
  ) {
    log.warn(
      `claude --print exit=${result.status} nhưng có response (${stdoutTrimmed.length} chars). Accept với caution.`,
    );
    return { ok: true };
  }

  // Exit non-zero — parse stderr+stdout để classify.
  const reason = classifyQuotaError(`${stderr}\n${stdout}`);

  // Khi unknown → dump cả stderr + stdout (truncated 500) qua log.warn (không phải
  // log.dim) để user dev thấy ngay. log.dim quá mờ trên terminal nhiều màu.
  if (reason === "unknown") {
    log.warn(`[debug] claude --print exit=${result.status} signal=${result.signal ?? "none"}`);
    if (stderr.trim()) log.warn(`[debug] stderr: ${stderr.slice(0, 500)}`);
    if (stdout.trim()) log.warn(`[debug] stdout: ${stdout.slice(0, 300)}`);
  }

  return { ok: false, reason, detail: stderr.slice(0, 500) || stdout.slice(0, 500) };
}
